/**
 * CatalogProductModal — tekil katalog ürünü ekleme ve düzenleme.
 *
 * Ekleme ve düzenleme aynı bileşen: `product` doluysa düzenleme, boşsa ekleme.
 *
 * FİYAT ALANI YOK. Katalog referans listesidir; kullanıcı ürünü kendi stoğuna alırken
 * satış fiyatını kendisi girer.
 *
 * Barkod ana DB'de UNIQUE olduğu için kaydetmeden önce çakışma kontrol edilir ve hangi
 * ürünle çakıştığı gösterilir — aksi halde kullanıcı kaydete basınca ham bir hata görür.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import CatalogService from '../../../../services/CatalogService';
import GuideButton from '../../../common/guide/GuideButton';
import {
  CATALOG_UNIT_OPTIONS,
  CATALOG_VAT_RATE_OPTIONS,
  CATALOG_VAT_STATUS_OPTIONS,
} from '../types/catalog.types';
import type { CatalogCategory, CatalogProduct, CatalogSector } from '../types/catalog.types';

/** ÖKC departman numarası 1-8 aralığında; boş bırakılabilir. */
const DEPARTMENT_NO_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

interface FormState {
  name: string;
  barcode: string;
  catalogCode: string;
  sector: CatalogSector | null;
  category: CatalogCategory | null;
  vatRate: number;
  vatStatus: string;
  unit: string;
  departmentNo: number | null;
  imageUrl: string;
  isActive: boolean;
}

const emptyForm = (): FormState => ({
  name: '',
  barcode: '',
  catalogCode: '',
  sector: null,
  category: null,
  vatRate: 20,
  vatStatus: CATALOG_VAT_STATUS_OPTIONS[0],
  unit: 'Adet',
  departmentNo: null,
  imageUrl: '',
  isActive: true,
});

export interface CatalogProductModalProps {
  open: boolean;
  onClose: () => void;
  /** Düzenlenecek ürün; null ise yeni kayıt. */
  product: CatalogProduct | null;
  sectors: CatalogSector[];
  /** Kayıt başarılı olunca ana sayfa listeyi tazelesin. */
  onSaved: (message: string) => void;
}

const CatalogProductModal: React.FC<CatalogProductModalProps> = ({
  open,
  onClose,
  product,
  sectors,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [barcodeConflict, setBarcodeConflict] = useState<string | null>(null);
  const [checkingBarcode, setCheckingBarcode] = useState(false);

  const isEdit = !!product;

  /**
   * Sektör listesi ana sayfada tazelenince kimliği değişiyor. Effect bağımlılığına
   * konursa modal açıkken form sıfırlanır ve kullanıcının girdisi uçar; bu yüzden
   * yalnızca açılış anında ref üzerinden okunuyor.
   */
  const sectorsRef = useRef(sectors);
  sectorsRef.current = sectors;

  // Modal açılışında formu doldur. Düzenlemede sektör/kategori nesneleri Id üzerinden
  // eşlenir; ürün yanıtı yalnızca Id ve ad taşıyor.
  useEffect(() => {
    if (!open) return;

    setError(null);
    setBarcodeConflict(null);

    if (!product) {
      setForm(emptyForm());
      return;
    }

    setForm({
      name: product.name ?? '',
      barcode: product.barcode ?? '',
      catalogCode: product.catalogCode ?? '',
      sector: sectorsRef.current.find((s) => s.id === product.sectorId) ?? null,
      category: null, // kategoriler yüklendikten sonra aşağıdaki effect eşliyor
      vatRate: product.vatRate ?? 20,
      vatStatus: product.vatStatus || CATALOG_VAT_STATUS_OPTIONS[0],
      unit: product.unit || 'Adet',
      departmentNo: product.departmentNo ?? null,
      imageUrl: product.imageUrl ?? '',
      isActive: product.isActive ?? true,
    });
  }, [open, product]);

  // Kategoriler sektöre bağlı kademeli yüklenir.
  const sectorId = form.sector?.id ?? null;
  useEffect(() => {
    if (!open || sectorId === null) {
      setCategories([]);
      return;
    }

    let cancelled = false;
    CatalogService.getCategories(sectorId)
      .then((rows) => {
        if (cancelled) return;
        setCategories(rows);
        // Düzenlemede ürünün kategorisini listeden eşle.
        if (product?.categoryId) {
          const match = rows.find((c) => c.id === product.categoryId);
          if (match) setForm((prev) => (prev.category ? prev : { ...prev, category: match }));
        }
      })
      .catch((err) => {
        console.error('Kategoriler yüklenemedi:', err);
        if (!cancelled) setCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, sectorId, product]);

  /** Üst kategoriler önce, altları girintili — Autocomplete tek seviyeli liste bekliyor. */
  const orderedCategories = useMemo(() => {
    const roots = categories.filter((c) => !c.parentId);
    const childrenOf = new Map<number, CatalogCategory[]>();
    categories.forEach((c) => {
      if (!c.parentId) return;
      if (!childrenOf.has(c.parentId)) childrenOf.set(c.parentId, []);
      childrenOf.get(c.parentId)!.push(c);
    });

    const ordered: CatalogCategory[] = [];
    roots.forEach((root) => {
      ordered.push(root);
      (childrenOf.get(root.id) ?? []).forEach((child) => ordered.push(child));
    });
    categories.forEach((c) => {
      if (!ordered.some((o) => o.id === c.id)) ordered.push(c);
    });
    return ordered;
  }, [categories]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Barkod alanından çıkınca çakışma kontrolü. Kaydetmeden önce hangi ürünle çakıştığını
   * göstermek için çakışan kaydın adı ayrıca çekilir.
   */
  const handleBarcodeBlur = useCallback(async () => {
    const barcode = form.barcode.trim();
    setBarcodeConflict(null);
    if (!barcode) return;

    setCheckingBarcode(true);
    try {
      const map = await CatalogService.checkBarcodes([barcode]);
      const conflictId = map[barcode];
      // Düzenlemede ürünün kendi barkodu çakışma sayılmaz.
      if (!conflictId || (product && conflictId === product.id)) return;

      const clash = await CatalogService.getProductById(conflictId);
      setBarcodeConflict(`Bu barkod "${clash.name}" ürününde zaten kayıtlı.`);
    } catch (err) {
      // Kontrol başarısız olsa da kayıt denenebilir; backend UNIQUE index'i yine yakalar.
      console.error('Barkod kontrolü yapılamadı:', err);
    } finally {
      setCheckingBarcode(false);
    }
  }, [form.barcode, product]);

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      setError('Ürün adı zorunludur.');
      return;
    }
    if (!form.sector) {
      setError('Sektör seçilmelidir.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        barcode: form.barcode.trim() || null,
        catalogCode: form.catalogCode.trim() || null,
        sectorId: form.sector.id,
        categoryId: form.category?.id ?? null,
        vatRate: form.vatRate,
        vatStatus: form.vatStatus,
        unit: form.unit.trim() || 'Adet',
        departmentNo: form.departmentNo,
        imageUrl: form.imageUrl.trim() || null,
        isActive: form.isActive,
      };

      if (isEdit && product) {
        await CatalogService.updateProduct({ ...payload, id: product.id });
        onSaved('Ürün güncellendi.');
      } else {
        await CatalogService.createProduct(payload);
        onSaved('Ürün oluşturuldu.');
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Kayıt başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const acSlotProps = { popper: { disablePortal: true } };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Inventory2Icon color="primary" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
          {isEdit ? 'Katalog Ürününü Düzenle' : 'Yeni Katalog Ürünü'}
        </Typography>
        <GuideButton guideKey="superadmin.catalogProduct" size="small" />
        <IconButton size="small" onClick={onClose} aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              required
              autoFocus
              label="Ürün Adı"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Barkod"
              value={form.barcode}
              onChange={(e) => setField('barcode', e.target.value)}
              onBlur={handleBarcodeBlur}
              error={!!barcodeConflict}
              helperText={barcodeConflict ?? (checkingBarcode ? 'Kontrol ediliyor...' : ' ')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCode2Icon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Katalog Kodu"
              value={form.catalogCode}
              onChange={(e) => setField('catalogCode', e.target.value)}
              helperText=" "
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              options={sectors}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={form.sector}
              onChange={(_, newValue) =>
                // Sektör değişince önceki kategori başka sektöre ait kalır.
                setForm((prev) => ({ ...prev, sector: newValue, category: null }))
              }
              slotProps={acSlotProps}
              renderInput={(params) => <TextField {...params} required label="Sektör" />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              disabled={!form.sector}
              options={orderedCategories}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={form.category}
              onChange={(_, newValue) => setField('category', newValue)}
              slotProps={acSlotProps}
              noOptionsText="Bu sektörde kategori yok"
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                return (
                  <li key={option.id} {...optionProps}>
                    <Typography
                      variant="body2"
                      sx={{ pl: option.parentId ? 2 : 0, fontWeight: option.parentId ? 400 : 600 }}
                    >
                      {option.name}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={form.sector ? 'Kategori' : 'Kategori (önce sektör seçin)'}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              options={CATALOG_VAT_RATE_OPTIONS}
              getOptionLabel={(option) => `%${option}`}
              value={form.vatRate}
              onChange={(_, newValue) => setField('vatRate', newValue ?? 20)}
              slotProps={acSlotProps}
              renderInput={(params) => <TextField {...params} label="KDV Oranı" />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              options={CATALOG_VAT_STATUS_OPTIONS}
              value={form.vatStatus}
              onChange={(_, newValue) =>
                setField('vatStatus', newValue ?? CATALOG_VAT_STATUS_OPTIONS[0])
              }
              slotProps={acSlotProps}
              renderInput={(params) => <TextField {...params} label="KDV Durumu" />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={[...CATALOG_UNIT_OPTIONS]}
              inputValue={form.unit}
              onInputChange={(_, newValue) => setField('unit', newValue)}
              slotProps={acSlotProps}
              renderInput={(params) => (
                <TextField {...params} label="Birim" helperText="Listede yoksa yazabilirsiniz" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              size="small"
              options={DEPARTMENT_NO_OPTIONS}
              getOptionLabel={(option) => String(option)}
              value={form.departmentNo}
              onChange={(_, newValue) => setField('departmentNo', newValue)}
              slotProps={acSlotProps}
              renderInput={(params) => (
                <TextField {...params} label="ÖKC Departman No" helperText="Opsiyonel" />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Görsel Adresi"
              placeholder="https://..."
              value={form.imageUrl}
              onChange={(e) => setField('imageUrl', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setField('isActive', e.target.checked)}
                />
              }
              label={form.isActive ? 'Aktif' : 'Pasif'}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Katalogda fiyat tutulmaz. Kullanıcı ürünü kendi stoğuna alırken satış fiyatını girer.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={onClose}>
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || checkingBarcode || !!barcodeConflict}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogProductModal;
